import { formatPlainDate, PlainDate } from "@sa-nu/shared-utils/date";

import {
  graphql,
  useFragment,
  type FragmentType,
} from "../../../../__generated__/gql";

const Fragment = graphql(`
  fragment TicketReservationConditions on StayTicketUnit {
    reservationInfo {
      type
      name
      waiveCleaningFee
      waiveConsumedSuppliesFee
      waiveDogFee
      waiveSaunaFee
      waiveOnsenFee
      waiveEvChargerFee
    }
    stayTickets {
      id
      stayEndNight
    }
    stayStartNight
  }
`);

export type TicketReservationConditionType = FragmentType<typeof Fragment>;

export function TicketReservationConditions({
  stayTicketUnitData,
}: {
  stayTicketUnitData?: TicketReservationConditionType | null;
}) {
  const stayTicketUnit = useFragment(Fragment, stayTicketUnitData);

  if (!stayTicketUnit) {
    return (
      <div className="border-sbeige w-full border p-[20px] text-[10px]">
        情報取得中...
      </div>
    );
  }

  const { stayTickets, reservationInfo, stayStartNight } = stayTicketUnit;

  // チケットの宿泊期間の最後の日付を表示したいという要件だったが
  // バックエンド側で予約可能な最後の日付に設定されていたので再計算して表示
  // https://github.com/sa-nu/2h/issues/1923
  const stayEndNight = stayTickets
    .map((ticket) => ticket.stayEndNight)
    .reduce((latest, current) => {
      return PlainDate.compare(latest, current) < 0 ? current : latest;
    });

  const {
    name,
    waiveConsumedSuppliesFee,
    waiveCleaningFee,
    waiveDogFee,
    waiveEvChargerFee,
    waiveSaunaFee,
    waiveOnsenFee,
  } = reservationInfo;

  const stayPeriod = `${formatPlainDate(stayStartNight)} 〜 ${formatPlainDate(
    stayEndNight,
  )}`;

  const list: { label?: string; value: string }[] = [
    {
      label: "チケット名",
      value: name,
    },
    {
      label: "予約の宿泊期間（泊数）",
      value: `${stayPeriod}（${stayTickets.length}泊）`,
    },
    {
      value:
        "※期間内で、最大3〜6ヶ月後（拠点により異なります）までの予約を取ることができます。",
    },
  ];

  if (reservationInfo.type === "Regular") {
    if (!waiveConsumedSuppliesFee)
      list.push({
        value: "※有料備品ご利用の場合請求あり",
      });
    if (!waiveCleaningFee)
      list.push({
        value: "※清掃料金請求あり",
      });
    if (!waiveDogFee)
      list.push({
        value: "※愛犬同伴の場合請求あり",
      });
    if (!waiveSaunaFee)
      list.push({
        value: "※サウナ付き施設に宿泊の場合請求あり",
      });
    if (!waiveOnsenFee)
      list.push({
        value: "※天然温泉付き施設に宿泊の場合請求あり",
      });
    if (!waiveEvChargerFee)
      list.push({
        value: "※EV充電器ご利用の場合請求あり",
      });
  }

  return (
    <div className="border-sbeige w-full border p-[20px]">
      <table className="text-[10px]">
        <tbody>
          {list.map((item, index) => (
            <tr key={index}>
              {item.label && (
                <th className="pr-2 text-left align-top font-bold text-nowrap">
                  {item.label}
                </th>
              )}
              <td className="align-top" colSpan={item.label ? 1 : 2}>
                {item.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
